import React, { useState, useEffect, useRef } from 'react';
import { googleApi } from '../utils/api';
import { useToast } from '../hooks/useToast';

export default function Settings() {
  const toast = useToast();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const loadStatus = () =>
    googleApi.getStatus().then(r => {
      setAccounts(r.data.accounts || []);
    }).finally(() => setLoading(false));

  useEffect(() => { loadStatus(); }, []);

  const handleConnect = async () => {
    try {
      const res = await googleApi.getAuthUrl();
      window.open(res.data.url, '_blank', 'width=600,height=700');
      const before = accounts.length;
      let fired = false;
      pollRef.current = setInterval(async () => {
        if (fired) return;
        const status = await googleApi.getStatus();
        const newAccounts = status.data.accounts || [];
        if (newAccounts.length > before) {
          fired = true;
          clearInterval(pollRef.current);
          setAccounts(newAccounts);
          toast('Google account connected!', 'success');
        }
      }, 2000);
      setTimeout(() => clearInterval(pollRef.current), 120000);
    } catch (e) {
      toast(e.response?.data?.error || 'Error connecting', 'error');
    }
  };

  const handleDisconnect = async (email) => {
    if (!window.confirm(`Disconnect ${email}? Any clients using this account will need to be reassigned.`)) return;
    await googleApi.disconnect(email);
    setAccounts(prev => prev.filter(a => a.email !== email));
    toast(`Disconnected ${email}`, 'success');
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>Settings</h1>
        <p>Connect Google accounts to enable Gmail drafts and Google Sheets sync</p>
      </div>

      <div className="card" style={{ maxWidth: 560 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
          <div style={{ width: 42, height: 42, borderRadius: 10, background: '#fff', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>G</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>Google Accounts</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Gmail (drafts) + Google Sheets (sync)</div>
          </div>
          {accounts.length > 0 && (
            <span className={`badge ${accounts.some(a => a.expired) ? 'badge-amber' : 'badge-green'}`}>
              {accounts.filter(a => !a.expired).length}/{accounts.length} active
            </span>
          )}
        </div>

        {loading ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading...</div>
        ) : (
          <>
            {accounts.length === 0 ? (
              <div style={{ background: 'var(--amber-soft)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 'var(--radius-sm)', padding: '12px 16px', marginBottom: 16, fontSize: 13, color: 'var(--amber)' }}>
                No accounts connected. Connect a Google account to use the app.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {accounts.map(account => (
                  <div key={account.email} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)',
                    padding: '10px 14px', border: '1px solid var(--border)'
                  }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                      background: 'var(--green-soft)', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontWeight: 700, fontSize: 14, color: 'var(--green)'
                    }}>
                      {(account.email || '?').charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {account.email || 'Unknown email'}
                      </div>
                      {account.connectedAt && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          Connected {new Date(account.connectedAt).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    {account.expired ? (
                      <>
                        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--amber)', background: 'var(--amber-soft)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 4, padding: '2px 8px', whiteSpace: 'nowrap' }}>
                          ✗ Expired
                        </span>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={handleConnect}
                          style={{ flexShrink: 0 }}
                        >
                          Reconnect
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="badge badge-green" style={{ fontSize: 11 }}>✓ Active</span>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDisconnect(account.email)}
                          style={{ flexShrink: 0 }}
                        >
                          Disconnect
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            <button className="btn btn-primary" onClick={handleConnect}>
              + Connect {accounts.length > 0 ? 'Another' : 'Google'} Account
            </button>
          </>
        )}
      </div>

      <div className="card" style={{ maxWidth: 560, marginTop: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>About PitchFlow</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
          All your data is stored locally in <code style={{ background: 'var(--bg-elevated)', padding: '1px 6px', borderRadius: 4 }}>data/db.json</code>. Nothing is sent anywhere except Google when syncing sheets or creating drafts.
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.8 }}>
          To assign a Google account to a specific client, open the client's profile and use the "Send emails from" dropdown.
        </div>
      </div>
    </div>
  );
}
